import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AppBar,
  Box,
  Chip,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import api from '../../infrastructure/config/api';
import { watchlistService } from '../../application/services/watchlist-service';
import { clearSymbolCache, getAllTradingPairs } from '../../infrastructure/utils/trading-pairs';

const BINANCE_FUTURES_24H = 'https://fapi.binance.com/fapi/v1/ticker/24hr';
const TRADING_VIEW_CHART_URL = 'https://www.tradingview.com/chart/';

function buildTradingViewUrl(symbol) {
  const tradingViewSymbol = `BINANCE:${String(symbol || '').toUpperCase()}.P`;
  return `${TRADING_VIEW_CHART_URL}?symbol=${encodeURIComponent(tradingViewSymbol)}`;
}

function openTradingView(symbol) {
  window.open(buildTradingViewUrl(symbol), '_blank', 'noopener,noreferrer');
}

function formatPrice(value) {
  if (value === null || value === undefined || value === '') return '...';
  const number = Number(value);
  if (!Number.isFinite(number)) return '...';
  return number.toLocaleString(undefined, { maximumFractionDigits: 8 });
}

function formatPercent(value) {
  if (value === null || value === undefined || value === '') return '...';
  const number = Number(value);
  if (!Number.isFinite(number)) return '...';
  const sign = number > 0 ? '+' : '';
  return `${sign}${number.toFixed(2)}%`;
}

function formatCompactUsdt(value) {
  if (value === null || value === undefined || value === '') return '...';
  const number = Number(value);
  if (!Number.isFinite(number)) return '...';
  return `${new Intl.NumberFormat(undefined, {
    notation: 'compact',
    maximumFractionDigits: 2,
  }).format(number)} USDT`;
}

async function loadFuturesMarketData() {
  const response = await fetch(BINANCE_FUTURES_24H);
  if (!response.ok) throw new Error('Cannot load Binance 24h market data');
  const data = await response.json();
  return new Map(
    (Array.isArray(data) ? data : [])
      .map((item) => {
        const price = Number(item.lastPrice);
        const change24h = Number(item.priceChangePercent);
        const quoteVolume = Number(item.quoteVolume);

        return [
          String(item.symbol || '').toUpperCase(),
          {
            price: Number.isFinite(price) ? price : null,
            change24h: Number.isFinite(change24h) ? change24h : null,
            quoteVolume: Number.isFinite(quoteVolume) ? quoteVolume : null,
          },
        ];
      })
      .filter(([symbol]) => Boolean(symbol))
  );
}

async function loadBinanceSymbols() {
  const response = await api.get('/api/symbols/binance-names');
  if (response.data.success && Array.isArray(response.data.data)) {
    return response.data.data;
  }
  throw new Error('Cannot load Binance symbols');
}

export default function MarketSymbolsPage() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [symbols, setSymbols] = useState([]);
  const [marketData, setMarketData] = useState(() => new Map());
  const [loading, setLoading] = useState(true);
  const [priceError, setPriceError] = useState('');
  const [query, setQuery] = useState('');
  const [roeMultiplier, setRoeMultiplier] = useState(10);
  const [sortDirection, setSortDirection] = useState('desc');
  const [watchedSymbols, setWatchedSymbols] = useState(() => new Set());
  const [actionLoadingSymbol, setActionLoadingSymbol] = useState('');

  const load = useCallback(async ({ force = false } = {}) => {
    setLoading(true);
    setPriceError('');
    try {
      if (force) clearSymbolCache();
      const [symbolNames, marketMap] = await Promise.all([
        loadBinanceSymbols().catch(async () => getAllTradingPairs()),
        loadFuturesMarketData().catch((error) => {
          setPriceError(error.message || 'Không tải được giá Binance');
          return new Map();
        }),
      ]);

      setSymbols(symbolNames);
      setMarketData(marketMap);
    } catch (error) {
      enqueueSnackbar(error.response?.data?.error || error.message || 'Không tải được danh sách symbols', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  const loadWatchlist = useCallback(async () => {
    try {
      const items = await watchlistService.list();
      setWatchedSymbols(new Set(items.map((item) => String(item.symbol).toUpperCase())));
    } catch (error) {
      console.error('Failed to load watchlist:', error);
    }
  }, []);

  useEffect(() => {
    load();
    loadWatchlist();
  }, [load, loadWatchlist]);

  const handleAddToWatchlist = useCallback(async (symbol) => {
    const normalizedSymbol = String(symbol || '').toUpperCase();
    try {
      if (watchedSymbols.has(normalizedSymbol)) {
        enqueueSnackbar(`${normalizedSymbol} đã tồn tại trong danh sách symbols`, { variant: 'warning' });
        return;
      }
      setActionLoadingSymbol(normalizedSymbol);
      await watchlistService.add(normalizedSymbol, 'LONG');
      setWatchedSymbols((current) => new Set(current).add(normalizedSymbol));
      enqueueSnackbar(`Đã thêm ${normalizedSymbol} LONG vào danh sách symbols`, { variant: 'success' });
    } catch (error) {
      console.error('Failed to add symbol to watchlist:', error);
      enqueueSnackbar(error.response?.data?.error || 'Không thể thêm symbol vào danh sách', { variant: 'error' });
    } finally {
      setActionLoadingSymbol('');
    }
  }, [enqueueSnackbar, watchedSymbols]);

  const handleRemoveFromWatchlist = useCallback(async (symbol) => {
    const normalizedSymbol = String(symbol || '').toUpperCase();
    try {
      setActionLoadingSymbol(normalizedSymbol);
      await watchlistService.remove(normalizedSymbol);
      setWatchedSymbols((current) => {
        const next = new Set(current);
        next.delete(normalizedSymbol);
        return next;
      });
      enqueueSnackbar(`Đã xóa ${normalizedSymbol} khỏi danh sách symbols`, { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(error.response?.data?.error || 'Không thể xóa symbol khỏi danh sách', { variant: 'error' });
    } finally {
      setActionLoadingSymbol('');
    }
  }, [enqueueSnackbar]);

  const filteredSymbols = useMemo(() => {
    const keyword = query.trim().toUpperCase();
    const matchedSymbols = keyword
      ? symbols.filter((symbol) => String(symbol).toUpperCase().includes(keyword))
      : symbols;

    return [...matchedSymbols].sort((a, b) => {
      const aChange = marketData.get(a)?.change24h;
      const bChange = marketData.get(b)?.change24h;
      const safeA = Number.isFinite(aChange) ? aChange : -Infinity;
      const safeB = Number.isFinite(bChange) ? bChange : -Infinity;
      return sortDirection === 'asc' ? safeA - safeB : safeB - safeA;
    });
  }, [marketData, query, sortDirection, symbols]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#1B1D20' }}>
      <AppBar
        position="sticky"
        elevation={1}
        sx={{
          bgcolor: '#1F2227',
          backgroundImage: 'none',
          borderBottom: '1px solid #2A2D33',
          color: '#E6E7EB',
        }}
      >
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/dashboard')} aria-label="Quay lại dashboard">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, ml: 1, fontWeight: 800 }}>
            636 Symbols Coin
          </Typography>
          <Tooltip title="Tải lại symbols">
            <IconButton color="inherit" onClick={() => load({ force: true })} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3 } }}>
        <Paper sx={{ p: 2, mb: 2, bgcolor: '#1F2227', border: '1px solid #2A2D33', borderRadius: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
            <TextField
              fullWidth
              size="small"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search symbol, ví dụ BTCUSDT"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
              <TextField
                size="small"
                type="number"
                label="ROE x"
                value={roeMultiplier}
                onChange={(event) => setRoeMultiplier(Number(event.target.value) || 0)}
                inputProps={{ min: 0, step: 1 }}
                sx={{ width: 100 }}
              />
              <Tooltip title={sortDirection === 'asc' ? 'Đang tăng dần, bấm để giảm dần' : 'Đang giảm dần, bấm để tăng dần'}>
                <IconButton
                  size="small"
                  onClick={() => setSortDirection((current) => current === 'asc' ? 'desc' : 'asc')}
                  aria-label={sortDirection === 'asc' ? 'Sắp xếp tăng dần' : 'Sắp xếp giảm dần'}
                  sx={{
                    width: 40,
                    height: 40,
                    color: '#90caf9',
                    border: '1px solid rgba(144, 202, 249, 0.55)',
                    bgcolor: 'rgba(144, 202, 249, 0.06)',
                    '&:hover': {
                      bgcolor: 'rgba(144, 202, 249, 0.12)',
                      borderColor: '#90caf9',
                    },
                  }}
                >
                  {sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Chip label={`${symbols.length} symbols`} sx={{ bgcolor: 'rgba(255, 255, 255, 0.08)', color: '#D3D7DE', fontWeight: 700 }} />
              <Chip variant="outlined" label={`${filteredSymbols.length} showing`} sx={{ color: '#90caf9', borderColor: 'rgba(144, 202, 249, 0.55)', fontWeight: 700 }} />
            </Stack>
          </Stack>
        </Paper>

        {priceError ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Vẫn load danh sách symbols, nhưng chưa lấy được giá hiện tại từ Binance.
          </Alert>
        ) : null}

        {loading ? (
          <Box textAlign="center" py={8}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={1.5}>
            {filteredSymbols.map((symbol) => {
              const data = marketData.get(symbol) || {};
              const change24h = data.change24h;
              const roe = Number.isFinite(change24h) ? change24h * roeMultiplier : null;
              const isUp = Number(change24h) >= 0;
              const metricColor = isUp ? '#66bb6a' : '#ff3b30';
              const metricBg = isUp ? 'rgba(102, 187, 106, 0.14)' : 'rgba(255, 59, 48, 0.14)';
              const metricBorder = isUp ? 'rgba(102, 187, 106, 0.55)' : 'rgba(255, 59, 48, 0.55)';
              const normalizedSymbol = String(symbol || '').toUpperCase();
              const watched = watchedSymbols.has(normalizedSymbol);
              const actionBusy = actionLoadingSymbol === normalizedSymbol;

              return (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  lg={3}
                  key={symbol}
                  sx={{ '@media (min-width: 1200px)': { flexBasis: '20%', maxWidth: '20%' } }}
                >
                  <Paper
                    elevation={3}
                    sx={{
                      height: '100%',
                      minHeight: 208,
                      p: 1.75,
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                      overflow: 'hidden',
                      bgcolor: '#242424',
                      borderRadius: 2,
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      boxShadow: '0 12px 26px rgba(0,0,0,0.28)',
                      transition: 'transform 160ms ease, border-color 160ms ease, box-shadow 160ms ease',
                      '&:before': {
                        content: '""',
                        position: 'absolute',
                        inset: '0 auto 0 0',
                        width: 5,
                        bgcolor: metricColor,
                      },
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        borderColor: metricBorder,
                        boxShadow: '0 16px 34px rgba(0,0,0,0.34)',
                      },
                    }}
                  >
                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h5" noWrap title={symbol} sx={{ fontWeight: 900, letterSpacing: 0.2, pl: 0.5 }}>
                          {symbol}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 700, letterSpacing: 1, pl: 0.5 }}>
                          USDT PERPETUAL
                        </Typography>
                      </Box>
                      <Stack spacing={0.75} alignItems="flex-end" sx={{ flexShrink: 0 }}>
                        <Chip
                          size="small"
                          label={formatPercent(change24h)}
                          sx={{
                            color: metricColor,
                            bgcolor: metricBg,
                            border: `1px solid ${metricBorder}`,
                            fontWeight: 900,
                          }}
                        />
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="Mở chart">
                            <IconButton size="small" color="primary" onClick={() => openTradingView(symbol)} aria-label={`Mở chart ${symbol}`}>
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={watched ? 'Xóa khỏi danh sách symbols' : 'Thêm vào danh sách symbols'}>
                            <span>
                              <IconButton
                                size="small"
                                color={watched ? 'error' : 'success'}
                                onClick={() => watched ? handleRemoveFromWatchlist(symbol) : handleAddToWatchlist(symbol)}
                                disabled={actionBusy}
                                aria-label={watched ? `Xóa ${symbol}` : `Thêm ${symbol}`}
                              >
                                {actionBusy ? <CircularProgress size={18} color="inherit" /> : (watched ? <DeleteOutlineIcon fontSize="small" /> : <AddIcon fontSize="small" />)}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    </Stack>

                    <Box sx={{ mt: 2, mb: 1.5, pl: 0.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 800, letterSpacing: 1 }}>
                        GIÁ GẦN NHẤT
                      </Typography>
                      <Typography variant="h4" sx={{ mt: 0.5, fontWeight: 900, lineHeight: 1, fontSize: { xs: '2rem', lg: '1.8rem' } }}>
                        {formatPrice(data.price)}
                        <Typography component="span" color="text.secondary" sx={{ ml: 0.8, fontSize: '1rem', fontWeight: 700 }}>
                          USDT
                        </Typography>
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={1} sx={{ mb: 1.5, pl: 0.5 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 800 }}>
                          24H
                        </Typography>
                        <Typography sx={{ color: metricColor, fontWeight: 900 }}>
                          {formatPercent(change24h)}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 800 }}>
                          ROE x{roeMultiplier}
                        </Typography>
                        <Typography sx={{ color: metricColor, fontWeight: 900 }}>
                          {formatPercent(roe)}
                        </Typography>
                      </Box>
                    </Stack>

                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, pl: 0.5, fontWeight: 700 }}>
                      Volume 24h: {formatCompactUsdt(data.quoteVolume)}
                    </Typography>

                    <Box sx={{ mt: 'auto' }} />
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Container>
    </Box>
  );
}
